import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import type { ReactNode } from "react";
import styled from "styled-components";

import { Number } from "@/utils";

const customTextFieldClasses = {
    root: "lights-map-input-test",
};

const StyledTextField = styled(TextField)`
    && .MuiInputLabel-shrink {
        transform: translate(0, 1.5px);
        font-size: 14px;
    }
    && .MuiInputLabel-root {
        white-space: nowrap;
        color: #999999;
    }
`;

interface InputProps {
    /**
     * The unit label displayed as an adornment.
     */
    unit?: string;
    /**
     * The input type ("text" or "number").
     */
    type?: string;
    /**
     * The current input value.
     */
    value?: unknown;
    /**
     * Additional CSS class name.
     */
    className?: string;
    /**
     * Whether the field is required.
     */
    required?: boolean;
    /**
     * The input label text.
     */
    label?: string;
    /**
     * Whether the input is disabled.
     */
    disabled?: boolean;
    /**
     * Whether to restrict to integers only.
     */
    integerOnly?: boolean;
    /**
     * The maximum number of decimal digits.
     */
    decimalDigits?: number;
    /**
     * The change event handler.
     */
    onChange?: (value: string) => void;
    /**
     * The blur event handler.
     */
    onBlur?: (value: string) => void;
    /**
     * Whether to allow scientific notation.
     */
    scientificNotation?: boolean;
}

/**
 * Styled text/number input field with optional unit adornment and number validation.
 *
 * @param {InputProps} props - The component props.
 * @returns {ReactNode} The rendered input component.
 */
const Input = ({
    unit,
    type,
    value,
    className = "",
    required,
    label,
    disabled,
    integerOnly,
    decimalDigits = 4,
    onChange = () => null,
    onBlur = () => null,
    scientificNotation = true,
}: InputProps): ReactNode => (
    <StyledTextField
        required={required}
        className={className}
        label={label}
        disabled={disabled}
        classes={customTextFieldClasses}
        InputProps={{
            endAdornment: unit && (
                <InputAdornment position="end">{unit}</InputAdornment>
            ),
        }}
        type={type === "number" ? "text" : type}
        value={value ? value.toString() : ""}
        onChange={(e) =>
            onChangeValue(
                e.target.value,
                value,
                {
                    type,
                    integerOnly,
                    decimalDigits,
                    onChange,
                    onBlur,
                    scientificNotation,
                },
                "onChange",
            )
        }
        onBlur={(e) =>
            onChangeValue(
                e.target.value,
                value,
                {
                    type,
                    integerOnly,
                    decimalDigits,
                    onChange,
                    onBlur,
                    scientificNotation,
                },
                "onBlur",
            )
        }
    />
);

interface InputChangeOptions {
    /**
     * The input type ("text" or "number").
     */
    type?: string;
    /**
     * Whether to restrict to integers only.
     */
    integerOnly?: boolean;
    /**
     * The maximum number of decimal digits.
     */
    decimalDigits?: number;
    /**
     * The change callback.
     */
    onChange?: (value: string) => void;
    /**
     * The blur callback.
     */
    onBlur?: (value: string) => void;
    /**
     * Whether to allow scientific notation.
     */
    scientificNotation?: boolean;
}

/**
 * Handles input value changes with number formatting and conversion logic.
 *
 * @param {string} newValue - The new input value.
 * @param {unknown} originalValue - The previous input value.
 * @param {InputChangeOptions} options - The input configuration options.
 * @param {"onChange" | "onBlur"} eventName - The event name that triggered the change.
 */
const onChangeValue = (
    newValue: string,
    originalValue: unknown,
    {
        type,
        integerOnly,
        decimalDigits,
        onChange,
        onBlur,
        scientificNotation,
    }: InputChangeOptions,
    eventName: "onChange" | "onBlur",
) => {
    const events = {
        onChange,
        onBlur,
    };
    let value = newValue;
    if (type === "number" && value) {
        value = Number.convert(
            value,
            originalValue,
            integerOnly,
            decimalDigits,
            false,
            scientificNotation,
        ).toString();
        if (eventName === "onBlur") {
            if (value === "-") {
                value = "";
            }
            if (value && value[value.length - 1] === ".") {
                value = value.substring(0, value.length - 1);
            } else {
                onChange(value);
            }
        }
    }
    if (events[eventName]) events[eventName](value);
};

export default Input;
